import { Tooltip } from '@mantine/core';
import React, { forwardRef, useEffect, useState } from 'react';
import type { AvatarSize } from '@/components/cards/Avatar';
import Avatar, { AvatarShape } from '@/components/cards/Avatar';
import { CHAIN_ID } from '@/constants/constants';
import useNftMeta from '@/hooks/useNftMeta';
import useWindowSize from '@/hooks/useWindowSize';
import type { CollectionDeals } from '@/utilities/__generated__/graphql';
import { BucketType, createBucketURL } from '@/utilities/createBucketUrl';
import { cleanAddress, getAlchemyNetwork } from '@/utilities/functions';
import { getTailwindColor } from '@/utilities/tailwind';

interface AvatarGroupProps {
  data: AvatarItem[];
  size: AvatarSize;
  linkTemplate?: string;
  showBorder?: boolean;
  showCount?: boolean;
}

export interface AvatarItem {
  nft_contract_address: string;
  token_id?: string;
  alt?: string | null;
  count?: number;
}

interface AvatarProps {
  src?: string | null;
  item: AvatarItem;
  size: AvatarSize;
  linkTemplate?: string;
  showBorder?: boolean;
  showCount?: boolean;
}

const AvatarDisplay = forwardRef<HTMLDivElement, AvatarProps>(
  ({ item, size, linkTemplate, showBorder, showCount }, ref) => {
    const isCollection = !item.token_id;
    const cleanedAddress = cleanAddress(item.nft_contract_address);

    const nftMeta = useNftMeta({
      collectionAddress: isCollection ? null : item.nft_contract_address,
      tokenId: isCollection ? null : item.token_id,
      network: isCollection ? null : getAlchemyNetwork(CHAIN_ID),
    });

    const nftThumbnail = !isCollection ? nftMeta?.nftThumbnail : null;

    const src = isCollection
      ? createBucketURL(BucketType.Collection, cleanedAddress)
      : nftThumbnail;
    const link = cleanedAddress
      ? linkTemplate?.replace('{0}', cleanedAddress)
      : undefined;

    return (
      <div ref={ref}>
        <Tooltip
          label={item.alt}
          color={getTailwindColor('primary').hover}
          transitionProps={{ transition: 'slide-up', duration: 300 }}
          openDelay={100}
        >
          <div className="pointer-events-auto">
            <Avatar
              shape={AvatarShape.square}
              size={size}
              address={cleanedAddress}
              alt={item.alt ?? 'avatar'}
              src={src}
              link={link}
              showBorder={showBorder}
              count={showCount !== false ? item.count : undefined}
            />
          </div>
        </Tooltip>
      </div>
    );
  }
);
AvatarDisplay.displayName = 'AvatarDisplay';

const AvatarGroup = ({
  data,
  size,
  linkTemplate,
  showBorder,
  showCount,
}: AvatarGroupProps) => {
  const zStack = ['z-30', 'z-20', 'z-10'];
  const windowSize = useWindowSize();
  const [avatarGroupCount, setAvatarGroupCount] = useState(2);

  useEffect(() => {
    setAvatarGroupCount(windowSize.width < 640 ? 2 : 3);
  }, [windowSize.width]);

  return (
    <>
      <div className="z-10 flex -space-x-4">
        {data.slice(0, avatarGroupCount).map((item, index) => (
          <div key={index} className={zStack[index]}>
            <AvatarDisplay
              item={item}
              size={size}
              linkTemplate={linkTemplate}
              showBorder={showBorder}
              showCount={showCount}
            />
          </div>
        ))}
        {data.length > avatarGroupCount ? (
          <div className="relative z-0 bg-clip-border">
            <div className="flex h-[38px] w-[38px] place-content-center items-center rounded-lg bg-white pl-4 text-md text-primary-main md:h-[56px] md:w-[56px] md:pl-0 lg:text-lg">
              <div>+{data.length - avatarGroupCount}</div>
            </div>
          </div>
        ) : undefined}
      </div>
    </>
  );
};

export const convertDealsToAvatarItems = (
  collectionDeals: Partial<CollectionDeals>[]
): AvatarItem[] => {
  return collectionDeals.map((deal) => {
    const nftAddress = deal.Collection?.nft_contract_address ?? '';
    const altName = deal.Collection?.name;
    return {
      nft_contract_address: nftAddress,
      alt: altName,
      count: deal.count ?? 0,
    };
  });
};

export default AvatarGroup;
